// MF-67 — Create/Edit Journal Category: the kind of record / who issues it (Merit, Teacher Demerit…).
// It belongs to a type (inherited by its severities, then templates and entries) and holds the
// visibility matrix. `code` is the stable analytics key and can only be set on create.
import { useEffect, useState } from 'react'
import { configPath } from '../paths'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { Box, Button, CircularProgress, TextField } from '@mui/material'
import FormCard from '../components/FormCard'
import FormField from '../components/FormField'
import FormSection from '../components/FormSection'
import VisibilityMatrix from '../components/VisibilityMatrix'
import TypeChoice from '../components/TypeChoice'
import { useTypes } from '../components/useLookups'
import { inputSx } from '../components/formStyles'
import { getCategory, createCategory, updateCategory } from '../../../../services/journal/categories.service'

const slug = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

const emptyVisibility = () => ({
  graph: { student: false, parent: false, staff: true },
  summary: { student: false, parent: false, staff: true }
})

const CategoryForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState('')
  const { list: types } = useTypes()
  const [visibility, setVisibility] = useState(emptyVisibility())
  const [saving, setSaving] = useState(false)
  const [loadingData, setLoadingData] = useState(Boolean(id))

  useEffect(() => {
    if (!id) return
    getCategory(id)
      .then(({ data }) => {
        if (!data) return
        setName(data.name || '')
        setCode(data.code || '')
        setDescription(data.description || '')
        setType(data.type || '')
        setVisibility({ ...emptyVisibility(), ...(data.visibility || {}) })
      })
      .catch(() => {})
      .finally(() => setLoadingData(false))
  }, [id])

  // New category: default to the first type once they load.
  useEffect(() => {
    if (!id && !type && types.length) setType(types[0].code)
  }, [id, type, types])

  // The code (stable analytics key) is derived from the name on create and never shown or edited.
  useEffect(() => {
    if (!id) setCode(slug(name))
  }, [name, id])

  const canSave = name.trim() && code.trim() && type && !saving

  const onSave = async () => {
    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        type,
        visibility
      }
      if (id) await updateCategory(id, payload)
      else await createCategory({ ...payload, code })
      enqueueSnackbar('Category saved', { variant: 'success' })
      navigate(configPath('categories'))
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.error?.description || 'Could not save category', {
        variant: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      {loadingData ? (
        <div className='flex justify-center' style={{ padding: '40px 0' }}>
          <CircularProgress />
        </div>
      ) : (
        <div style={{ padding: '24px 8px', maxWidth: 880, margin: '0 auto' }}>
          <FormCard
            title={id ? 'Category details' : 'New category'}
            description='The kind of record, or who issues it (e.g. Merit, Teacher Demerit). It sets the type; its severities are the degrees inside it.'
            footer={
              <>
                <Button color='inherit' onClick={() => navigate(configPath('categories'))}>
                  Cancel
                </Button>
                <Button variant='contained' disabled={!canSave} onClick={onSave}>
                  {saving ? 'Saving…' : 'Save category'}
                </Button>
              </>
            }
          >
            <Box className='grid' style={{ gap: 20 }} sx={{ gridTemplateColumns: '1fr' }}>
              <FormField label='Name' required htmlFor='category-name'>
                <TextField
                  id='category-name'
                  size='small'
                  fullWidth
                  autoFocus={!id}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={inputSx}
                />
              </FormField>
            </Box>

            <TypeChoice
              value={type}
              onChange={setType}
              required
              hint='Every severity in this category inherits it (e.g. Merit → Positive, Teacher Demerit → Negative).'
            />

            <FormField
              label='Description'
              htmlFor='category-description'
              hint='What belongs in this category, e.g. “Demerits issued by a classroom teacher”.'
            >
              <TextField
                id='category-description'
                size='small'
                fullWidth
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={inputSx}
              />
            </FormField>

            <FormSection
              title='Who can see it'
              description='Which audiences see this category in graphs and in the summary.'
            >
              <VisibilityMatrix value={visibility} onChange={setVisibility} />
            </FormSection>
          </FormCard>
        </div>
      )}
    </>
  )
}

export default CategoryForm
